import { HttpRequestError } from "@nktkas/hyperliquid";
import * as Sentry from "@sentry/react";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { isKnownHyperliquidRejection } from "#/features/trade/lib/hyperliquid-error.ts";
import { isUnauthorizedError } from "#/lib/api/api.ts";
import { notifyIfRateLimit } from "#/lib/api/notify-rate-limit.ts";

function extractHttpTags(error: unknown): Record<string, string> {
	const tags: Record<string, string> = {};
	if (!(error instanceof HttpRequestError)) return tags;
	const status = error.response?.status;
	if (status) tags.http_status = String(status);
	if (error.response?.url) {
		try {
			tags.api_endpoint = new URL(error.response.url).pathname.replace(
				/^\//,
				"",
			);
		} catch {
			/* ignore */
		}
	}
	return tags;
}

/**
 * QueryClient 팩토리 — getRouter가 라우터마다 하나씩 만든다. 서버(Workers)는 요청마다
 * 새 라우터/클라이언트: 아이솔레이트 공유 싱글턴이면 한 방문자의 캐시(/auth/me·잔고·글)가
 * 다른 방문자의 SSR HTML에 섞인다. 클라이언트는 탭당 한 번 호출된다(기존과 동일).
 * QueryClientProvider는 router.tsx의 ssr-query 통합이 주입한다.
 */
export function createQueryClient(): QueryClient {
	const queryClient = new QueryClient({
		queryCache: new QueryCache({
			onError: (error, query) => {
				if (notifyIfRateLimit(error)) return;
				// Expired/invalid session 401 — apiFetch clears the token and
				// AuthSync re-logins, then this gated query refetches. Self-healing
				// noise; real re-login failures surface separately, not here.
				if (isUnauthorizedError(error)) return;
				const httpTags = extractHttpTags(error);
				const key = query.queryKey;
				const domain = Array.isArray(key) ? String(key[0] ?? "") : "";
				const type = Array.isArray(key) ? String(key[1] ?? "") : "";

				Sentry.captureException(error, {
					tags: {
						source: "tanstack-query",
						query_domain: domain,
						query_type: type,
						...httpTags,
					},
					...(httpTags.http_status && Number(httpTags.http_status) >= 500
						? { fingerprint: [domain, type, httpTags.http_status] }
						: {}),
					contexts: {
						query: {
							queryHash: query.queryHash,
							queryKey: query.queryKey,
						},
					},
				});
			},
		}),
		mutationCache: new MutationCache({
			onError: (error, _vars, _ctx, mutation) => {
				if (notifyIfRateLimit(error)) return;
				// Expired/invalid session 401 (see QueryCache handler) — self-heals.
				if (isUnauthorizedError(error)) return;
				// Known business-logic rejections (reduce-only would increase
				// position, min-notional, insufficient margin, …) are expected
				// outcomes already surfaced to the user via the mutation's local
				// onError toast — drop them so they don't add Error-level noise to
				// Sentry. Unknown rejections fall through and are still captured.
				if (isKnownHyperliquidRejection(error)) return;
				const httpTags = extractHttpTags(error);

				Sentry.captureException(error, {
					tags: {
						source: "tanstack-mutation",
						...httpTags,
					},
					...(httpTags.http_status && Number(httpTags.http_status) >= 500
						? { fingerprint: ["mutation", httpTags.http_status] }
						: {}),
					contexts: {
						mutation: {
							mutationKey: mutation.options.mutationKey,
						},
					},
				});
			},
		}),
	});

	// Prevent TanStack Query from garbage-collecting the xyz (HIP-3) account
	// cache.  This key has no useQuery observer — it is populated via
	// setQueryData inside useHyperAccount's queryFn and read by the main-DEX
	// WS sync.  Without this, the default gcTime (5 min) races against the
	// refetchInterval (also 5 min), creating windows where HIP-3 balance/
	// position data disappears.
	queryClient.setQueryDefaults(["hyper", "account-xyz"], {
		gcTime: Number.POSITIVE_INFINITY,
	});

	return queryClient;
}
